import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { studentsAPI, usersAPI, settingsAPI, documentsAPI, prospectsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import Modal from '../../components/Common/Modal';
import {
  isValidPassportNumber,
  PASSPORT_FORMAT_MESSAGE,
  normalizePassportInput
} from '../../utils/passportValidation';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Country, State, City } from 'country-state-city';
import { CITY_SELECT_OTHER_VALUE, getCityFieldState } from '../../utils/citySelect';
import { downloadStudentProfilePdf } from '../../utils/studentProfilePdf';
import { getDocumentFileSizeError } from '../../utils/uploadLimits';
import { normalizeApiErrorMessage } from '../../utils/apiErrorMessage';

/** Default interview language from address country (ISO 3166-1 alpha-2): India → English, France → French. */
function defaultLanguageForCountryIso(isoCode) {
  if (!isoCode) return 'ENGLISH';
  const code = String(isoCode).toUpperCase();
  if (code === 'FR') return 'FRENCH';
  if (code === 'IN') return 'ENGLISH';
  return 'ENGLISH';
}

const StudentForm = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const config = getConfig();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  // Whether the STUDENT's own country (selected in the form) is France.
  // Used to gate France-specific sections such as the France Travail follow-up
  // so they only appear when the student actually resides in France.
  const role = String(user?.role || '').toLowerCase();
  const isInterviewerEdit = isEdit && role === 'interviewer';
  const [loading, setLoading] = useState(false);
  const [salesTeam, setSalesTeam] = useState([]);
  const [tiedUpAgencies, setTiedUpAgencies] = useState([]);
  const [sources, setSources] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courseCampusFees, setCourseCampusFees] = useState([]);
  const [internshipPrograms, setInternshipPrograms] = useState([]);
  const [statusDesiredOptions, setStatusDesiredOptions] = useState([]);
  const [howFoundOptions, setHowFoundOptions] = useState([]);
  const [businessSpecializations, setBusinessSpecializations] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [requiredDocFiles, setRequiredDocFiles] = useState({});
  const [requiredDocVerification, setRequiredDocVerification] = useState({});
  const [requiredDocVerifying, setRequiredDocVerifying] = useState({});
  const [uploadedRequiredDocTypes, setUploadedRequiredDocTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [phoneE164, setPhoneE164] = useState('');
  const [cityEntryMode, setCityEntryMode] = useState('list');
  const [postSubmitModalOpen, setPostSubmitModalOpen] = useState(false);
  const [createdStudentId, setCreatedStudentId] = useState(null);
  const [createdStudentSnapshot, setCreatedStudentSnapshot] = useState(null);
  const initialStudentType = (searchParams.get('student_type') || 'b2c').toLowerCase() === 'b2b' ? 'b2b' : 'b2c';
  const initialAgencyId = searchParams.get('agency_id') || '';
  const lockAgencySelection = String(searchParams.get('lock_agency') || '') === '1';
  const [studentType, setStudentType] = useState(initialStudentType);
  const [passportDuplicate, setPassportDuplicate] = useState(null);
  const [passportChecking, setPassportChecking] = useState(false);
  const passportCheckSeq = useRef(0);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const isAdminUser = ['super_admin', 'admin', 'manager'].includes(String(user?.role || '').toLowerCase());

  const [form, setForm] = useState({
    title: 'Mr.', first_name: '', last_name: '', dob: '',
    address: '', city: '', state: '', country: config.label, zip_code: '',
    phone: '', phone_country_code: config.defaultPhoneCode, email: '',
    passport_no: '', gender: '', language: defaultLanguageForCountryIso(config.flagCode),
    current_degree: '',
    source: '',
    intake_id: '', program_type: '', course_id: '', campus_id: '', agency_id: initialStudentType === 'b2b' ? initialAgencyId : '', owner_id: '', formation_type: '',
    internship_program: '', status_desired: '', business_specialization: '',
    social_security_number: '',
    parent_address: '', parent_zip_code: '', parent_city: '', parent_state: '', parent_country: '',
    parent_location_country_code: '', parent_location_state_code: '',
    father_phone: '', mother_phone: '', is_disabled: '', how_found_school: '', it_knowledge: '',
    org_type: '', org_type_other: '', org_town: '', org_department: '',
    org_advisor_name: '', org_advisor_email: '', org_advisor_phone: '', org_advisor_phone_country_code: '',
    org_registered_france_travail: '', org_registration_date: '', org_identification_number: '',
    org_compensated_france_travail: '', org_compensated_until_date: '',
    studies_followed: [
      { level: 'High School', year: '', establishment: '', degree_title: '', comments: '' },
      { level: 'Bachelor', year: '', establishment: '', degree_title: '', comments: '' },
      { level: 'Mastere', year: '', establishment: '', degree_title: '', comments: '' },
      { level: 'Other', year: '', establishment: '', degree_title: '', comments: '' }
    ],
    modern_languages: [
      { language: '', written: '', oral: '', duration: '' },
      { language: '', written: '', oral: '', duration: '' },
      { language: '', written: '', oral: '', duration: '' }
    ],
    stays_abroad: [
      { country: '', duration: '', year: '', activities: '' },
      { country: '', duration: '', year: '', activities: '' },
      { country: '', duration: '', year: '', activities: '' },
      { country: '', duration: '', year: '', activities: '' }
    ],
    work_internships: [
      { company: '', duration_year: '', missions: '' },
      { company: '', duration_year: '', missions: '' },
      { company: '', duration_year: '', missions: '' },
      { company: '', duration_year: '', missions: '' },
      { company: '', duration_year: '', missions: '' }
    ],
    cultural_activities: [
      { activity: '', years: '', level: '' },
      { activity: '', years: '', level: '' },
      { activity: '', years: '', level: '' },
      { activity: '', years: '', level: '' },
      { activity: '', years: '', level: '' }
    ],
    location_country_code: config.flagCode, location_state_code: ''
  });

  // France-specific sections (e.g. France Travail organization follow-up) must
  // only render when the STUDENT resides in France, regardless of the CRM
  // country context.
  const isFranceStudent = useMemo(() => (
    String(form.location_country_code || '').toUpperCase() === 'FR'
    || String(form.country || '').trim().toLowerCase() === 'france'
  ), [form.location_country_code, form.country]);

  const states = useMemo(
    () => (form.location_country_code ? State.getStatesOfCountry(form.location_country_code) : []),
    [form.location_country_code]
  );
  const cities = useMemo(
    () => (form.location_country_code && form.location_state_code ? City.getCitiesOfState(form.location_country_code, form.location_state_code) : []),
    [form.location_country_code, form.location_state_code]
  );

  const parentStates = useMemo(
    () => (form.parent_location_country_code ? State.getStatesOfCountry(form.parent_location_country_code) : []),
    [form.parent_location_country_code]
  );
  const parentCities = useMemo(
    () => (form.parent_location_country_code && form.parent_location_state_code ? City.getCitiesOfState(form.parent_location_country_code, form.parent_location_state_code) : []),
    [form.parent_location_country_code, form.parent_location_state_code]
  );
  const lockB2bOwnerOnCreate = !isEdit && studentType === 'b2b';
  const selectedB2bAgency = useMemo(() => {
    if (studentType !== 'b2b' || !form.agency_id) return null;
    return tiedUpAgencies.find((a) => String(a.lead_id) === String(form.agency_id)) || null;
  }, [studentType, form.agency_id, tiedUpAgencies]);

  useEffect(() => {
    if (isEdit || studentType !== 'b2b' || !form.agency_id) return;
    const agency = tiedUpAgencies.find((a) => String(a.lead_id) === String(form.agency_id));
    if (!agency) return;
    const nextOwnerId = agency.owner_id ? String(agency.owner_id) : '';
    setForm((f) => (String(f.owner_id) === nextOwnerId ? f : { ...f, owner_id: nextOwnerId }));
  }, [isEdit, studentType, form.agency_id, tiedUpAgencies]);

  useEffect(() => {
    const promises = [
      usersAPI.getSalesTeam(),
      settingsAPI.getIntakes(),
      settingsAPI.getTiedUpAgencies(),
      settingsAPI.getSources(),
      settingsAPI.getProgramTypes(),
      settingsAPI.getCourses(),
      settingsAPI.getCampuses(),
      settingsAPI.getCourseCampusFees(),
      settingsAPI.getDocumentTypes({ category: 'documents' }),
      ...(isFranceCRM ? [settingsAPI.getInternshipPrograms(), settingsAPI.getStatusDesiredOptions(), settingsAPI.getBusinessSpecializations(), settingsAPI.getHowFoundOptions()] : [])
    ];
    Promise.all(promises).then((results) => {
      const [u, i, t, src, pt, c, cp, ccf, dt, ...rest] = results;
      setSalesTeam(u.data);
      setIntakes(i.data);
      setTiedUpAgencies(t.data);
      setSources((src.data || []).filter(x => x.status === 'active'));
      setProgramTypes((pt.data || []).filter(x => x.status === 'active'));
      setCourses((c.data || []).filter(x => x.status === 'active'));
      setCampuses((cp.data || []).filter(x => x.status === 'active'));
      setCourseCampusFees((ccf.data || []).filter(x => x.status === 'active'));
      setRequiredDocuments(
        (dt.data || []).filter(x => x.status === 'active' && Number(x.is_required) === 1)
      );
      if (isFranceCRM && rest.length >= 3) {
        setInternshipPrograms((rest[0].data || []).filter(x => x.status === 'active'));
        setStatusDesiredOptions((rest[1].data || []).filter(x => x.status === 'active'));
        setBusinessSpecializations((rest[2].data || []).filter(x => x.status === 'active'));
        if (rest[3]) setHowFoundOptions((rest[3].data || []).filter(x => x.status === 'active'));
      }
    });

    const fromProspectId = searchParams.get('fromProspect');
    if (!isEdit && fromProspectId) {
      prospectsAPI.getById(fromProspectId).then(res => {
        const p = res.data;
        const cc = p.phone_country_code || config.defaultPhoneCode;
        const nat = String(p.contact_number || '');
        const ccDigits = cc.replace(/^\+/, '');
        const cleanNat = nat.startsWith(ccDigits) ? nat.slice(ccDigits.length) : nat;
        setPhoneE164(cleanNat ? `${cc}${cleanNat}` : '');

        const pCountryCode = p.location_country_code || '';
        const pStateCode = p.location_state_code || '';
        if (pCountryCode && pStateCode) {
          const cityList = City.getCitiesOfState(pCountryCode, pStateCode);
          const { mode, cityForForm } = getCityFieldState(p.city || '', cityList);
          setCityEntryMode(mode);
          setForm(f => ({
            ...f,
            first_name: p.first_name || p.name || '',
            last_name: p.last_name || '',
            title: p.title || f.title,
            dob: p.dob ? String(p.dob).slice(0, 10) : '',
            email: p.email || '',
            phone_country_code: cc,
            passport_no: p.passport_no || '',
            gender: p.gender || '',
            language: p.language || f.language,
            current_degree: p.current_degree || '',
            source: p.source || '',
            address: p.address || '',
            country: p.country || f.country,
            state: p.state || '',
            city: cityForForm,
            zip_code: p.zip_code || '',
            location_country_code: pCountryCode,
            location_state_code: pStateCode
          }));
        } else {
          setForm(f => ({
            ...f,
            first_name: p.first_name || p.name || '',
            last_name: p.last_name || '',
            title: p.title || f.title,
            dob: p.dob ? String(p.dob).slice(0, 10) : '',
            email: p.email || '',
            phone_country_code: cc,
            passport_no: p.passport_no || '',
            gender: p.gender || '',
            language: p.language || f.language,
            current_degree: p.current_degree || '',
            source: p.source || '',
            address: p.address || '',
            country: p.country || f.country,
            state: p.state || '',
            city: p.city || '',
            zip_code: p.zip_code || ''
          }));
        }
      }).catch(() => {});
    }

    if (isEdit) {
      studentsAPI.getById(id).then(res => {
        const s = res.data;
        const normalizedCountryName = typeof s.country === 'string' ? s.country.trim().toLowerCase() : '';
        const normalizedStateName = typeof s.state === 'string' ? s.state.trim().toLowerCase() : '';
        const mappedCountry = s.country
          ? countries.find(c => c.name.trim().toLowerCase() === normalizedCountryName) || null
          : null;
        const nextLocationCountryCode = mappedCountry?.isoCode || config.flagCode;
        const mappedStates = nextLocationCountryCode ? State.getStatesOfCountry(nextLocationCountryCode) : [];
        const mappedState = s.state
          ? mappedStates.find(st => st.name.trim().toLowerCase() === normalizedStateName) || null
          : null;

        const cityList =
          nextLocationCountryCode && mappedState?.isoCode
            ? City.getCitiesOfState(nextLocationCountryCode, mappedState.isoCode)
            : [];
        const { mode: loadedCityMode, cityForForm } = getCityFieldState(s.city || '', cityList);

        const cc = s.phone_country_code || config.defaultPhoneCode;
        const nat = String(s.phone || '');
        const ccDigitsLoad = cc.replace(/^\+/, '');
        const cleanNat = nat.startsWith(ccDigitsLoad) ? nat.slice(ccDigitsLoad.length) : nat;
        const e164 = cleanNat ? `${cc}${cleanNat}` : '';
        setPhoneE164(e164);

        setForm({
          title: s.title, first_name: s.first_name, last_name: s.last_name,
          dob: s.dob ? s.dob.slice(0, 10) : '', address: s.address || '',
          city: cityForForm, state: s.state || '', country: s.country || config.label,
          zip_code: s.zip_code || '', phone: s.phone, phone_country_code: cc,
          email: s.email,
          passport_no: s.passport_no || '', gender: s.gender || '',
          language: s.language === 'FRENCH' ? 'FRENCH' : 'ENGLISH',
          current_degree: s.current_degree || '',
          source: s.source || '',
          intake_id: s.intake_id || '',
          program_type: s.applications?.[0]?.course_program_type || s.applications?.[0]?.program_type || '',
          course_id: s.applications?.[0]?.course_id || '',
          campus_id: s.campus_id || '', agency_id: s.agency_id || '', owner_id: s.owner_id || '',
          formation_type: s.formation_type || '',
          internship_program: s.internship_program || '',
          status_desired: s.status_desired || '',
          business_specialization: s.business_specialization || '',
          social_security_number: s.social_security_number || '',
          parent_address: s.parent_address || '', parent_zip_code: s.parent_zip_code || '',
          parent_city: s.parent_city || '', parent_state: s.parent_state || '', parent_country: s.parent_country || '',
          parent_location_country_code: (() => {
            if (!s.parent_country) return '';
            const pc = countries.find(c => c.name.trim().toLowerCase() === String(s.parent_country).trim().toLowerCase());
            return pc?.isoCode || '';
          })(),
          parent_location_state_code: (() => {
            if (!s.parent_country || !s.parent_state) return '';
            const pc = countries.find(c => c.name.trim().toLowerCase() === String(s.parent_country).trim().toLowerCase());
            if (!pc) return '';
            const pStates = State.getStatesOfCountry(pc.isoCode);
            const ps = pStates.find(st => st.name.trim().toLowerCase() === String(s.parent_state).trim().toLowerCase());
            return ps?.isoCode || '';
          })(),
          father_phone: s.father_phone || '', mother_phone: s.mother_phone || '',
          is_disabled: s.is_disabled || '', how_found_school: s.how_found_school || '', it_knowledge: s.it_knowledge || '',
          org_type: s.org_type || '',
          org_type_other: s.org_type_other || '',
          org_town: s.org_town || '',
          org_department: s.org_department || '',
          org_advisor_name: s.org_advisor_name || '',
          org_advisor_email: s.org_advisor_email || '',
          org_advisor_phone: s.org_advisor_phone || '',
          org_advisor_phone_country_code: s.org_advisor_phone_country_code || '',
          org_registered_france_travail: s.org_registered_france_travail || '',
          org_registration_date: s.org_registration_date ? String(s.org_registration_date).slice(0, 10) : '',
          org_identification_number: s.org_identification_number || '',
          org_compensated_france_travail: s.org_compensated_france_travail || '',
          org_compensated_until_date: s.org_compensated_until_date ? String(s.org_compensated_until_date).slice(0, 10) : '',
          studies_followed: s.studies_followed || [
            { level: 'High School', year: '', establishment: '', degree_title: '', comments: '' },
            { level: 'Bachelor', year: '', establishment: '', degree_title: '', comments: '' },
            { level: 'Mastere', year: '', establishment: '', degree_title: '', comments: '' },
            { level: 'Other', year: '', establishment: '', degree_title: '', comments: '' }
          ],
          modern_languages: s.modern_languages || [
            { language: '', written: '', oral: '', duration: '' },
            { language: '', written: '', oral: '', duration: '' },
            { language: '', written: '', oral: '', duration: '' }
          ],
          stays_abroad: s.stays_abroad || [
            { country: '', duration: '', year: '', activities: '' },
            { country: '', duration: '', year: '', activities: '' },
            { country: '', duration: '', year: '', activities: '' },
            { country: '', duration: '', year: '', activities: '' }
          ],
          work_internships: s.work_internships || [
            { company: '', duration_year: '', missions: '' },
            { company: '', duration_year: '', missions: '' },
            { company: '', duration_year: '', missions: '' },
            { company: '', duration_year: '', missions: '' },
            { company: '', duration_year: '', missions: '' }
          ],
          cultural_activities: s.cultural_activities || [
            { activity: '', years: '', level: '' },
            { activity: '', years: '', level: '' },
            { activity: '', years: '', level: '' },
            { activity: '', years: '', level: '' },
            { activity: '', years: '', level: '' }
          ],
          location_country_code: nextLocationCountryCode,
          location_state_code: mappedState?.isoCode || ''
        });
        setCityEntryMode(loadedCityMode);
        const uploadedNormalTypes = (s.documents || [])
          .filter((d) => (d.document_category || 'documents') === 'documents')
          .map((d) => String(d.document_type));
        setUploadedRequiredDocTypes(uploadedNormalTypes);
        setStudentType(s.student_type || (s.agency_id ? 'b2b' : 'b2c'));
      });
    }
  }, [id, isEdit, countries]);

  useEffect(() => {
    const pass = form.passport_no;
    if (!pass || !isValidPassportNumber(pass)) {
      passportCheckSeq.current += 1;
      setPassportDuplicate(null);
      setPassportChecking(false);
      return undefined;
    }
    const seq = ++passportCheckSeq.current;
    setPassportChecking(true);
    const t = setTimeout(async () => {
      try {
        const params = { passport_no: pass };
        if (isEdit && id) params.exclude_student_id = id;
        const res = await studentsAPI.checkPassport(params);
        if (seq !== passportCheckSeq.current) return;
        setPassportDuplicate(res.data);
      } catch {
        if (seq !== passportCheckSeq.current) return;
        setPassportDuplicate(null);
      } finally {
        if (seq === passportCheckSeq.current) setPassportChecking(false);
      }
    }, 450);
    return () => {
      clearTimeout(t);
      passportCheckSeq.current += 1;
      setPassportChecking(false);
    };
  }, [form.passport_no, isEdit, id]);

  useEffect(() => {
    const passportDoc = (requiredDocuments || []).find((d) => Number(d?.is_passport || 0) === 1);
    if (!passportDoc) return;
    const key = String(passportDoc.document_type_name || '');
    if (!key) return;
    setRequiredDocVerification((prev) => ({ ...prev, [key]: '' }));
  }, [form.passport_no, requiredDocuments]);

  const validate = () => {
    const errs = {};
    if (!form.first_name) errs.first_name = t('studentForm.validationRequired');
    if (!form.last_name) errs.last_name = t('studentForm.validationRequired');
    if (!phoneE164) errs.phone = t('studentForm.validationRequired');
    if (!form.email) errs.email = t('studentForm.validationRequired');
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = t('studentForm.invalidEmail');
    if (!form.passport_no) {
      errs.passport_no = t('studentForm.passportRequired');
    } else if (!isValidPassportNumber(form.passport_no)) {
      errs.passport_no = PASSPORT_FORMAT_MESSAGE;
    }
    if (!form.language || !['ENGLISH', 'FRENCH'].includes(form.language)) {
      errs.language = t('studentForm.validationRequired');
    }
    if (!isInterviewerEdit && !form.campus_id) errs.campus_id = t('studentForm.campusRequired');
    if (!isInterviewerEdit && !form.program_type) errs.program_type = t('studentForm.programRequired');
    if (!isInterviewerEdit && !form.course_id) errs.course_id = t('studentForm.courseRequired');
    if (!isInterviewerEdit && isFranceCRM && !form.formation_type) errs.formation_type = t('studentForm.formationTypeRequired');
    if (!isInterviewerEdit && studentType === 'b2b' && !form.agency_id) errs.agency_id = t('studentForm.agencyRequired');
    if (!isInterviewerEdit && requiredDocuments.length > 0) {
      const missing = requiredDocuments.filter((d) => {
        const key = String(d.document_type_name);
        const hasExisting = uploadedRequiredDocTypes.includes(key);
        const hasSelectedFile = Boolean(requiredDocFiles[key]);
        return !(hasExisting || hasSelectedFile);
      });
      if (missing.length > 0) {
        const names = missing
          .map((d) => String(d.document_type_name || '').trim())
          .filter(Boolean);
        let list = names.join(', ');
        const maxLen = 220;
        if (list.length > maxLen) list = `${list.slice(0, maxLen - 1)}…`;
        errs.required_documents = `${t('studentForm.missingDocumentsPrefix')}: ${list}`;
      }
      const passportDoc = requiredDocuments.find((d) => Number(d?.is_passport || 0) === 1);
      if (passportDoc) {
        const passportKey = String(passportDoc.document_type_name || '');
        const selectedPassportFile = requiredDocFiles[passportKey];
        if (selectedPassportFile && requiredDocVerification[passportKey] !== 'verified') {
          errs.required_documents = t('studentForm.passportNotVerified');
        }
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resolveStudentCountryForRules = () => {
    const iso = String(form.location_country_code || '').toUpperCase();
    const name = String(form.country || '').trim().toLowerCase();
    if (iso === 'FR' || name === 'france') return 'FRANCE';
    if (iso === 'IN' || name === 'india') return 'INDIA';
    return '';
  };

  const validateCountrySpecificUniqueness = async () => {
    const countryCode = resolveStudentCountryForRules();
    if (!countryCode || !form.passport_no || !isValidPassportNumber(form.passport_no)) {
      return true;
    }
    const phoneParts = splitPhoneForApi(phoneE164);
    const params = {
      country_code: countryCode,
      passport_no: form.passport_no,
      email: form.email,
      phone: phoneParts.phone,
      phone_country_code: phoneParts.phone_country_code
    };
    if (isEdit && id) params.exclude_student_id = id;
    const res = await studentsAPI.checkDuplicates(params);
    const dup = res?.data || {};
    const nextErrors = {};
    if (dup.passport_exists) nextErrors.passport_no = 'Passport number already exists.';
    if (countryCode === 'FRANCE') {
      if (dup.email_exists) nextErrors.email = 'Email already exists for a France student.';
      if (dup.phone_exists) nextErrors.phone = 'Contact number already exists for a France student.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return false;
    }
    return true;
  };

  const formatFirstName = (value) => {
    const v = String(value || '');
    if (!v) return '';
    return v.charAt(0).toUpperCase() + v.slice(1);
  };

  const formatLastName = (value) => String(value || '').toUpperCase();

  const uploadSelectedRequiredDocs = async (studentId) => {
    const entries = Object.entries(requiredDocFiles).filter(([, file]) => Boolean(file));
    if (!entries.length) return;
    const verificationMap = {};
    for (const [documentType, file] of entries) {
      const docMeta = requiredDocuments.find((d) => String(d.document_type_name || '') === String(documentType));
      const isPassportDoc = Number(docMeta?.is_passport || 0) === 1;
      if (isPassportDoc) {
        setRequiredDocVerifying((prev) => ({ ...prev, [documentType]: true }));
      }
      const formData = new FormData();
      formData.append('document_type', documentType);
      formData.append('document_category', 'documents');
      formData.append('file', file);
      try {
        const res = await documentsAPI.upload(studentId, formData);
        const verified = !!res?.data?.verification?.verified;
        verificationMap[documentType] = verified ? 'verified' : 'not_verified';
      } catch (err) {
        verificationMap[documentType] = 'not_verified';
        setRequiredDocVerification((prev) => ({ ...prev, ...verificationMap }));
        throw err;
      } finally {
        if (isPassportDoc) {
          setRequiredDocVerifying((prev) => ({ ...prev, [documentType]: false }));
        }
      }
    }
    setRequiredDocVerification((prev) => ({ ...prev, ...verificationMap }));
  };

  const triggerImmediatePassportVerification = async (documentType, file) => {
    const cc = resolveStudentCountryForRules() || String(activeCountry || '').toUpperCase() || 'INDIA';
    const formData = new FormData();
    formData.append('document_type', String(documentType || ''));
    formData.append('passport_no', String(form.passport_no || ''));
    formData.append('country_code', cc);
    formData.append('file', file);
    console.log('[Passport Verify][StudentForm] Selected file:', {
      document_type: documentType,
      file_name: file?.name,
      file_type: file?.type,
      file_size: file?.size,
      country_code: cc
    });
    setRequiredDocVerifying((prev) => ({ ...prev, [documentType]: true }));
    try {
      const res = await documentsAPI.verifyPassport(formData);
      const verified = !!res?.data?.verified;
      console.log('[Passport Verify][StudentForm] API response:', res?.data);
      console.log('[Passport Verify][StudentForm] Extracted passport number:', res?.data?.extracted_passport_no || null);
      setRequiredDocVerification((prev) => ({ ...prev, [documentType]: verified ? 'verified' : 'not_verified' }));
    } catch (err) {
      console.log('[Passport Verify][StudentForm] API error response:', err?.response?.data || null);
      setRequiredDocVerification((prev) => ({ ...prev, [documentType]: 'not_verified' }));
    } finally {
      setRequiredDocVerifying((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const isUnique = await validateCountrySpecificUniqueness();
    if (!isUnique) return;
    setLoading(true);
    try {
      const phoneParts = splitPhoneForApi(phoneE164);
      const data = {
        ...form,
        first_name: formatFirstName(form.first_name),
        last_name: formatLastName(form.last_name),
        phone: phoneParts.phone,
        phone_country_code: phoneParts.phone_country_code
      };
      if (isInterviewerEdit) {
        const interviewerPayload = {
          title: data.title,
          first_name: data.first_name,
          last_name: data.last_name,
          dob: data.dob,
          address: data.address,
          city: data.city,
          state: data.state,
          country: data.country,
          zip_code: data.zip_code,
          phone: data.phone,
          phone_country_code: data.phone_country_code,
          email: data.email,
          passport_no: data.passport_no,
          gender: data.gender,
          language: data.language,
          current_degree: data.current_degree,
          location_country_code: data.location_country_code,
          location_state_code: data.location_state_code
        };
        await studentsAPI.update(id, interviewerPayload);
        toast.success(t('studentForm.updateSuccess'));
        navigate(`/students/${id}`);
        return;
      }
      ['intake_id', 'owner_id', 'campus_id', 'course_id'].forEach(k => { if (data[k] === '') data[k] = null; });
      data.student_type = studentType;
      // Source is only for B2C students
      if (studentType !== 'b2c') {
        data.source = null;
      }
      // For non-B2B students, clear agency_id
      if (studentType !== 'b2b') {
        data.agency_id = null;
      } else {
        if (data.agency_id === '') data.agency_id = null;
      }

      if (isEdit) {
        await studentsAPI.update(id, data);
        try {
          await uploadSelectedRequiredDocs(id);
        } catch (uploadErr) {
          toast.error(
            `${t('studentForm.uploadPhaseFailed')}: ${normalizeApiErrorMessage(uploadErr, t('studentForm.saveFailed'))}`
          );
          toast.info(t('studentForm.studentSavedUploadsFailed'));
          navigate(`/students/${id}`);
          return;
        }
        toast.success(t('studentForm.updateSuccess'));
        navigate(`/students/${id}`);
      } else {
        const res = await studentsAPI.create(data);
        const newId = res.data.student_id;
        let uploadsOk = true;
        try {
          await uploadSelectedRequiredDocs(newId);
        } catch (uploadErr) {
          uploadsOk = false;
          toast.error(
            `${t('studentForm.uploadPhaseFailed')}: ${normalizeApiErrorMessage(uploadErr, t('studentForm.saveFailed'))}`
          );
          toast.info(t('studentForm.studentSavedUploadsFailed'));
        }
        const fromProspectId = searchParams.get('fromProspect');
        if (fromProspectId) {
          try {
            await prospectsAPI.convert(fromProspectId, { entity_type: 'student', entity_id: newId });
          } catch {}
        }
        if (uploadsOk) {
          toast.success(t('studentForm.createSuccess'));
        }
        setCreatedStudentId(newId);
        setCreatedStudentSnapshot({ ...data, student_id: newId });
        setPostSubmitModalOpen(true);
      }
    } catch (err) {
      toast.error(normalizeApiErrorMessage(err, t('studentForm.saveFailed')));
    }
    setLoading(false);
  };

  const handleAgencyChange = (agencyId) => {
    const agency = tiedUpAgencies.find((a) => String(a.lead_id) === String(agencyId));
    setForm((f) => ({
      ...f,
      agency_id: agencyId,
      owner_id: agency?.owner_id ? String(agency.owner_id) : ''
    }));
  };

  const handleChange = (field, value) => {
    if (field === 'campus_id') {
      const campusIdNum = Number(value || 0);
      if (campusIdNum > 0) {
        const allowedCourseIds = new Set(
          (courseCampusFees || [])
            .filter(m => Number(m.campus_id) === campusIdNum)
            .map(m => Number(m.course_id))
        );
        setForm(f => ({
          ...f,
          campus_id: value,
          program_type: '',
          course_id: f.course_id && allowedCourseIds.has(Number(f.course_id)) ? f.course_id : ''
        }));
      } else {
        setForm(f => ({ ...f, campus_id: value, program_type: '', course_id: '' }));
      }
    } else if (field === 'program_type') {
      const nextProgram = String(value || '');
      setForm(f => ({ ...f, program_type: nextProgram, course_id: '' }));
    } else {
      if (field === 'first_name') {
        setForm(f => ({ ...f, first_name: formatFirstName(value) }));
      } else if (field === 'last_name') {
        setForm(f => ({ ...f, last_name: formatLastName(value) }));
      } else if (field === 'passport_no') {
        setForm(f => ({ ...f, passport_no: normalizePassportInput(value) }));
      } else {
        setForm(f => ({ ...f, [field]: value }));
      }
    }
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const handleCountrySelect = (countryCode) => {
    setCityEntryMode('list');
    const selected = countries.find(c => c.isoCode === countryCode);
    setForm(f => ({
      ...f,
      location_country_code: countryCode,
      country: selected?.name || '',
      location_state_code: '',
      state: '',
      city: '',
      language: defaultLanguageForCountryIso(countryCode)
    }));
  };

  const handleStateSelect = (stateCode) => {
    setCityEntryMode('list');
    const selected = states.find(s => s.isoCode === stateCode);
    setForm(f => ({
      ...f,
      location_state_code: stateCode,
      state: selected?.name || '',
      city: ''
    }));
  };

  const handleCitySelect = (cityName) => {
    setForm(f => ({ ...f, city: cityName }));
  };

  const handlePhoneChange = (value) => {
    setPhoneE164(value || '');
    if (errors.phone) setErrors(e => ({ ...e, phone: '' }));
  };

  const splitPhoneForApi = (e164) => {
    if (!e164) return { phone_country_code: config.defaultPhoneCode, phone: '' };
    try {
      const parsed = parsePhoneNumber(e164);
      if (parsed?.countryCallingCode && parsed?.nationalNumber) {
        return { phone_country_code: `+${parsed.countryCallingCode}`, phone: parsed.nationalNumber };
      }
    } catch {}
    return { phone_country_code: config.defaultPhoneCode, phone: e164.replace(/^\+/, '') };
  };
  const filteredProgramTypes = useMemo(() => {
    const campusIdNum = Number(form.campus_id || 0);
    if (!campusIdNum) return [];
    const allowedCourseIds = new Set(
      (courseCampusFees || [])
        .filter(m => Number(m.campus_id) === campusIdNum)
        .map(m => Number(m.course_id))
    );
    const usedTypes = new Set(
      (courses || [])
        .filter(c => allowedCourseIds.has(Number(c.course_id)))
        .map(c => String(c.program_type || '').trim())
        .filter(Boolean)
    );
    return (programTypes || []).filter((p) => usedTypes.has(String(p.program_type_name || '').trim()));
  }, [form.campus_id, courses, courseCampusFees, programTypes]);

  const filteredCourses = useMemo(() => {
    const campusIdNum = Number(form.campus_id || 0);
    if (!campusIdNum || !form.program_type) return [];
    const allowedCourseIds = new Set(
      (courseCampusFees || [])
        .filter(m => Number(m.campus_id) === campusIdNum)
        .map(m => Number(m.course_id))
    );
    return (courses || []).filter(
      c => allowedCourseIds.has(Number(c.course_id)) && String(c.program_type || '') === String(form.program_type || '')
    );
  }, [form.campus_id, form.program_type, courses, courseCampusFees]);
  const selectedCourseCampusFee = useMemo(() => {
    const campusIdNum = Number(form.campus_id || 0);
    const courseIdNum = Number(form.course_id || 0);
    if (!campusIdNum || !courseIdNum) return null;
    return (courseCampusFees || []).find(
      (m) => Number(m.campus_id) === campusIdNum && Number(m.course_id) === courseIdNum
    ) || null;
  }, [form.campus_id, form.course_id, courseCampusFees]);

  return (
    <div>
      <Modal
        isOpen={postSubmitModalOpen}
        onClose={() => setPostSubmitModalOpen(false)}
        title={t('studentForm.studentCreated')}
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setPostSubmitModalOpen(false)}>{t('common.close')}</button>
            <button
              className="btn btn-outline"
              onClick={() => {
                try {
                  downloadStudentProfilePdf(createdStudentSnapshot, `student_${createdStudentSnapshot?.last_name || 'profile'}.pdf`);
                } catch (e) {
                  toast.error(t('studentForm.pdfFailed'));
                }
              }}
              disabled={!createdStudentSnapshot}
            >
              {t('studentForm.downloadPdf')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const sid = createdStudentId;
                setPostSubmitModalOpen(false);
                if (sid) navigate(`/students/${sid}`);
              }}
              disabled={!createdStudentId}
            >
              {t('studentForm.viewStudent')}
            </button>
          </>
        }
      >
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          {t('studentForm.createdModalBody')}
        </div>
      </Modal>

      <div className="page-header">
        <h1>{isEdit ? t('studentForm.editTitle') : t('studentForm.addTitle')}</h1>
      </div>

      {!isEdit && (
        <div className="card" style={{ marginBottom: 16, padding: 16, background: 'var(--info-light)', border: '1px solid #93C5FD' }}>
          <strong>{t('studentForm.directEntryTitle')}</strong> {t('studentForm.directEntryDesc')}
        </div>
      )}

      {/* B2B Toggle */}
      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontWeight: 600 }}>{t('studentForm.studentType')} <span className="required">*</span></label>
          <select
            value={studentType}
            disabled={lockAgencySelection || isInterviewerEdit}
            onChange={(e) => {
              const nextType = e.target.value;
              setStudentType(nextType);
              if (nextType !== 'b2b') setForm(f => ({ ...f, agency_id: '', owner_id: '' }));
            }}
          >
            <option value="b2c">{t('studentForm.b2cStudent')}</option>
            <option value="b2b">{t('studentForm.b2bStudent')}</option>
          </select>
          {studentType === 'b2b' && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {t('studentForm.b2bReferredDesc')}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* B2B Agency Selection - tied-up B2B leads serve as agencies */}
            {studentType === 'b2b' && (
              <div className="form-group full-width" style={{ marginBottom: 8 }}>
                <label>{t('studentForm.tiedUpAgency')} <span className="required">*</span></label>
                <select
                  value={form.agency_id}
                  disabled={lockAgencySelection || isInterviewerEdit}
                  onChange={(e) => handleAgencyChange(e.target.value)}
                  className={errors.agency_id ? 'error' : ''}>
                  <option value="">{t('studentForm.selectAgency')}</option>
                  {tiedUpAgencies.map(a => (
                    <option key={a.lead_id} value={a.lead_id}>
                      {a.agency_name} — {a.contact_person || t('common.na')} ({a.phone_country_code || t('common.na')})
                    </option>
                  ))}
                </select>
                {errors.agency_id && <div className="error-text">{errors.agency_id}</div>}
                {tiedUpAgencies.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 4 }}>
                    {t('studentForm.noAgenciesAvailable')}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label>{t('studentForm.titleLabel')}</label>
              <select value={form.title} onChange={(e) => handleChange('title', e.target.value)}>
                {[{ v: 'Mr.', l: t('studentForm.mr') }, { v: 'Ms.', l: t('studentForm.ms') }, { v: 'Mrs.', l: t('studentForm.mrs') }, { v: 'Dr.', l: t('studentForm.dr') }].map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('studentForm.firstName')} <span className="required">*</span></label>
              <input value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} className={errors.first_name ? 'error' : ''} />
              {errors.first_name && <div className="error-text">{errors.first_name}</div>}
            </div>
            <div className="form-group">
              <label>{t('studentForm.lastName')} <span className="required">*</span></label>
              <input value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} className={errors.last_name ? 'error' : ''} />
              {errors.last_name && <div className="error-text">{errors.last_name}</div>}
            </div>
            <div className="form-group">
              <label>{t('studentForm.dob')}</label>
              <input type="date" value={form.dob} onChange={(e) => handleChange('dob', e.target.value)} />
            </div>
            <div className="form-group full-width">
              <label>{t('common.address')}</label>
              <input value={form.address} onChange={(e) => handleChange('address', e.target.value)} placeholder={t('common.streetAddress')} />
            </div>
            <div className="form-group">
              <label>{t('studentForm.country')}</label>
              <select
                value={form.location_country_code || ''}
                onChange={(e) => handleCountrySelect(e.target.value)}
              >
                <option value="">{t('common.selectCountry')}</option>
                {countries.map(c => (
                  <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('studentForm.state')}</label>
              <select
                value={form.location_state_code || ''}
                onChange={(e) => handleStateSelect(e.target.value)}
                disabled={!form.location_country_code || states.length === 0}
              >
                <option value="">{states.length ? t('common.selectState') : t('common.selectCountryFirst')}</option>
                {states.map(s => (
                  <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('studentForm.city')}</label>
              {!form.location_state_code ? (
                <input type="text" disabled value="" placeholder={t('common.selectStateFirst')} />
              ) : cities.length > 0 ? (
                <>
                  <select
                    value={cityEntryMode === 'other' ? CITY_SELECT_OTHER_VALUE : (form.city || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === CITY_SELECT_OTHER_VALUE) {
                        setCityEntryMode('other');
                        setForm((f) => ({ ...f, city: '' }));
                      } else {
                        setCityEntryMode('list');
                        handleCitySelect(v);
                      }
                    }}
                  >
                    <option value="">{t('common.selectCity')}</option>
                    {cities.map((c) => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                    <option value={CITY_SELECT_OTHER_VALUE}>{t('common.otherEnterCity')}</option>
                  </select>
                  {cityEntryMode === 'other' && (
                    <input
                      type="text"
                      value={form.city || ''}
                      onChange={(e) => handleCitySelect(e.target.value)}
                      placeholder={t('common.typeCityName')}
                      style={{ marginTop: 8 }}
                    />
                  )}
                </>
              ) : (
                <input
                  value={form.city || ''}
                  onChange={(e) => handleCitySelect(e.target.value)}
                  placeholder={t('common.enterCity')}
                />
              )}
            </div>
            <div className="form-group">
              <label>{t('common.zipCode')}</label>
              <input value={form.zip_code} onChange={(e) => handleChange('zip_code', e.target.value)} />
            </div>

            {studentType === 'b2c' && (
              <div className="form-group">
                <label>{t('studentForm.source')}</label>
                <select
                  value={form.source || ''}
                  onChange={(e) => handleChange('source', e.target.value)}
                  disabled={sources.length === 0 || isInterviewerEdit}
                >
                  <option value="">{sources.length ? t('studentForm.selectSource') : t('studentForm.noSourcesConfigured')}</option>
                  {sources.map((s) => (
                    <option key={s.source_id || s.source_name} value={s.source_name}>
                      {s.source_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>{t('common.campus')} <span className="required">*</span></label>
              <select value={form.campus_id} onChange={(e) => handleChange('campus_id', e.target.value)} className={errors.campus_id ? 'error' : ''} disabled={isInterviewerEdit}>
                <option value="">{t('studentForm.selectCampus')}</option>
                {campuses.map(c => <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>)}
              </select>
              {errors.campus_id && <div className="error-text">{errors.campus_id}</div>}
            </div>
            <div className="form-group">
              <label>{t('studentForm.program')} <span className="required">*</span></label>
              <select
                value={form.program_type}
                onChange={(e) => handleChange('program_type', e.target.value)}
                className={errors.program_type ? 'error' : ''}
                disabled={!form.campus_id || isInterviewerEdit}
              >
                <option value="">{form.campus_id ? t('studentForm.selectProgram') : t('studentForm.selectCampusFirst')}</option>
                {filteredProgramTypes.map(p => (
                  <option key={p.program_type_id || p.program_type_name} value={p.program_type_name}>
                    {p.program_type_name}
                  </option>
                ))}
              </select>
              {errors.program_type && <div className="error-text">{errors.program_type}</div>}
            </div>
            <div className="form-group">
              <label>{t('common.course')} <span className="required">*</span></label>
              <select
                value={form.course_id}
                onChange={(e) => handleChange('course_id', e.target.value)}
                className={errors.course_id ? 'error' : ''}
                disabled={!form.campus_id || !form.program_type || isInterviewerEdit}
              >
                <option value="">
                  {!form.campus_id
                    ? t('studentForm.selectCampusFirst')
                    : form.program_type
                      ? t('studentForm.selectCourse')
                      : t('studentForm.selectProgramFirst')}
                </option>
                {filteredCourses.map(c => <option key={c.course_id} value={c.course_id}>{c.course_name}</option>)}
              </select>
              {errors.course_id && <div className="error-text">{errors.course_id}</div>}
            </div>
            <div className="form-group">
              <label>{t('studentForm.mappedTuitionFee')}</label>
              <input
                type="text"
                value={selectedCourseCampusFee ? `${selectedCourseCampusFee.default_fee} ${selectedCourseCampusFee.currency}` : '-'}
                readOnly
              />
            </div>
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.formationType')} <span className="required">*</span></label>
                <select
                  value={form.formation_type || ''}
                  onChange={(e) => handleChange('formation_type', e.target.value)}
                  className={errors.formation_type ? 'error' : ''}
                  disabled={isInterviewerEdit}
                >
                  <option value="">{t('studentForm.selectFormationType')}</option>
                  <option value="initial">{t('studentForm.formationInitial')}</option>
                  <option value="alternance">{t('studentForm.formationAlternance')}</option>
                </select>
                {errors.formation_type && <div className="error-text">{errors.formation_type}</div>}
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.internshipProgram')}</label>
                <select
                  value={form.internship_program || ''}
                  onChange={(e) => handleChange('internship_program', e.target.value)}
                  disabled={isInterviewerEdit}
                >
                  <option value="">{t('studentForm.selectInternshipProgram')}</option>
                  {internshipPrograms.map(p => (
                    <option key={p.id} value={p.program_name}>{p.program_name}</option>
                  ))}
                </select>
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.statusDesired')}</label>
                <select
                  value={form.status_desired || ''}
                  onChange={(e) => handleChange('status_desired', e.target.value)}
                  disabled={isInterviewerEdit}
                >
                  <option value="">{t('studentForm.selectStatusDesired')}</option>
                  {statusDesiredOptions.map(o => (
                    <option key={o.id} value={o.option_name}>{o.option_name}</option>
                  ))}
                </select>
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.businessSpecialization')}</label>
                <select
                  value={form.business_specialization || ''}
                  onChange={(e) => handleChange('business_specialization', e.target.value)}
                  disabled={isInterviewerEdit}
                >
                  <option value="">{t('studentForm.selectBusinessSpecialization')}</option>
                  {businessSpecializations.map(b => (
                    <option key={b.id} value={b.specialization_name}>{b.specialization_name}</option>
                  ))}
                </select>
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.socialSecurityNumber')}</label>
                <input
                  value={form.social_security_number || ''}
                  onChange={(e) => handleChange('social_security_number', e.target.value)}
                  disabled={isInterviewerEdit}
                />
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.isDisabled')}</label>
                <select
                  value={form.is_disabled || ''}
                  onChange={(e) => handleChange('is_disabled', e.target.value)}
                  disabled={isInterviewerEdit}
                >
                  <option value="">{t('common.select')}</option>
                  <option value="Yes">{t('common.yes')}</option>
                  <option value="No">{t('common.no')}</option>
                </select>
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group">
                <label>{t('studentForm.howFoundSchool')}</label>
                <select
                  value={form.how_found_school || ''}
                  onChange={(e) => handleChange('how_found_school', e.target.value)}
                  disabled={isInterviewerEdit}
                >
                  <option value="">{t('common.select')}</option>
                  {howFoundOptions.map((o) => {
                    const isFr = String(i18n.language || '').toLowerCase().startsWith('fr');
                    const label = isFr ? (o.option_name_fr || o.option_name) : o.option_name;
                    return <option key={o.id || o.option_name} value={o.option_name}>{label}</option>;
                  })}
                </select>
              </div>
            )}
            {isFranceCRM && (
              <div className="form-group full-width">
                <label>{t('studentForm.itKnowledge')}</label>
                <input
                  value={form.it_knowledge || ''}
                  onChange={(e) => handleChange('it_knowledge', e.target.value)}
                  placeholder={t('studentForm.itKnowledgePlaceholder')}
                  disabled={isInterviewerEdit}
                />
              </div>
            )}
            <div className="form-group">
              <label>{t('common.phone')} <span className="required">*</span></label>
              <PhoneInput
                international
                defaultCountry={config.flagCode}
                value={phoneE164 || undefined}
                onChange={handlePhoneChange}
                placeholder={t('studentForm.phonePlaceholder')}
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <div className="error-text">{errors.phone}</div>}
            </div>
            <div className="form-group">
              <label>{t('studentForm.email')} <span className="required">*</span></label>
              <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} className={errors.email ? 'error' : ''} />
              {errors.email && <div className="error-text">{errors.email}</div>}
            </div>
            <div className="form-group">
              <label>{t(isFranceCRM ? 'studentForm.passportNumberFrance' : 'studentForm.passportNumber')} <span className="required">*</span></label>
              <input
                value={form.passport_no}
                onChange={(e) => handleChange('passport_no', e.target.value)}
                placeholder={t('studentForm.passportPlaceholder')}
                className={errors.passport_no ? 'error' : ''}
                autoComplete="off"
              />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                {t('studentForm.passportUploadHint')}
              </p>
              {passportChecking && isValidPassportNumber(form.passport_no) && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>{t('studentForm.checkingPassport')}</p>
              )}
              {passportDuplicate?.exists && passportDuplicate.matches?.length > 0 && (
                <div
                  role="status"
                  style={{
                    marginTop: 10,
                    padding: 12,
                    borderRadius: 8,
                    background: 'var(--warning-light, #fffbeb)',
                    border: '1px solid var(--warning, #f59e0b)',
                    fontSize: 13,
                    lineHeight: 1.45
                  }}
                >
                  <strong style={{ color: 'var(--orange, #c2410c)' }}>{t('studentForm.passportExists')}</strong>
                  <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: 12 }}>
                    {t('studentForm.existingRecords')}
                  </p>
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
                    {passportDuplicate.matches.map((m) => (
                      <li key={m.student_id} style={{ marginBottom: 8 }}>
                        <Link to={`/students/${m.student_id}`} style={{ fontWeight: 600 }}>
                          {m.student_ref}
                        </Link>
                        {' · '}
                        {m.student_name || '—'}
                        {m.email ? ` · ${m.email}` : ''}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                          {t('studentForm.ownerLabel')} {m.owner_name || '—'}
                          {m.owner_email ? ` (${m.owner_email})` : ''}
                          {m.created_by_name ? (
                            <>
                              {' · '}
                              {t('studentForm.enteredBy')} {m.created_by_name}
                              {m.created_by_email ? ` (${m.created_by_email})` : ''}
                            </>
                          ) : (
                            <>{' · '}{t('studentForm.enteredBy')} —</>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {errors.passport_no && <div className="error-text">{errors.passport_no}</div>}
            </div>
            <div className="form-group">
              <label>{t('common.gender')}</label>
              <select value={form.gender} onChange={(e) => handleChange('gender', e.target.value)}>
                <option value="">{t('studentForm.selectGender')}</option>
                <option value="Male">{t('common.male')}</option>
                <option value="Female">{t('common.female')}</option>
                <option value="Other">{t('common.other')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                {t('studentForm.languageLabel')} <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <select
                value={form.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className={errors.language ? 'error' : ''}
              >
                <option value="ENGLISH">{t('studentForm.languageEnglish')}</option>
                <option value="FRENCH">{t('studentForm.languageFrench')}</option>
              </select>
              {errors.language && <div className="error-text">{errors.language}</div>}
            </div>
            <div className="form-group">
              <label>{t('studentForm.lastDegree')}</label>
              <input value={form.current_degree} onChange={(e) => handleChange('current_degree', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('common.intake')}</label>
              <select value={form.intake_id} onChange={(e) => handleChange('intake_id', e.target.value)} disabled={isInterviewerEdit}>
                <option value="">{t('studentForm.selectIntake')}</option>
                {intakes.map(i => <option key={i.intake_id} value={i.intake_id}>{i.intake_name}</option>)}
              </select>
            </div>

            {/* Show tied-up agency dropdown in edit mode for B2B */}
            {isEdit && studentType === 'b2b' && (
              <div className="form-group">
                <label>{t('studentForm.tiedUpAgency')}</label>
                <select value={form.agency_id} onChange={(e) => handleAgencyChange(e.target.value)} disabled={isInterviewerEdit}>
                  <option value="">{t('studentForm.noneTiedUp')}</option>
                  {tiedUpAgencies.map(a => (
                    <option key={a.lead_id} value={a.lead_id}>{a.agency_name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>{t('common.owner')}</label>
              {lockB2bOwnerOnCreate ? (
                <input
                  value={selectedB2bAgency?.owner_name || t('studentForm.defaultOwner')}
                  readOnly
                  disabled
                />
              ) : (
                <select
                  value={isAdminUser ? form.owner_id : ''}
                  onChange={(e) => handleChange('owner_id', e.target.value)}
                  disabled={!isAdminUser || isInterviewerEdit}
                  title={!isAdminUser ? t('studentForm.onlyAdminChangeOwner') : undefined}
                >
                  <option value="">{t('studentForm.defaultOwner')}</option>
                  {isAdminUser && salesTeam.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              )}
            </div>
          </div>

          {/* ─── FRANCE-ONLY: Parent Information ─── */}
          {isFranceCRM && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.parentInfo')}
              </h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>{t('studentForm.parentAddress')}</label>
                  <input value={form.parent_address || ''} onChange={(e) => handleChange('parent_address', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.parentCountry')}</label>
                  <select
                    value={form.parent_location_country_code || ''}
                    onChange={(e) => {
                      const code = e.target.value;
                      const selected = countries.find(c => c.isoCode === code);
                      setForm(f => ({
                        ...f,
                        parent_location_country_code: code,
                        parent_country: selected?.name || '',
                        parent_location_state_code: '',
                        parent_state: '',
                        parent_city: ''
                      }));
                    }}
                  >
                    <option value="">{t('common.selectCountry')}</option>
                    {countries.map(c => (
                      <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('studentForm.parentState')}</label>
                  <select
                    value={form.parent_location_state_code || ''}
                    onChange={(e) => {
                      const code = e.target.value;
                      const selected = parentStates.find(s => s.isoCode === code);
                      setForm(f => ({
                        ...f,
                        parent_location_state_code: code,
                        parent_state: selected?.name || '',
                        parent_city: ''
                      }));
                    }}
                    disabled={!form.parent_location_country_code || parentStates.length === 0}
                  >
                    <option value="">{parentStates.length ? t('common.selectState') : t('common.selectCountryFirst')}</option>
                    {parentStates.map(s => (
                      <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('studentForm.parentCity')}</label>
                  {!form.parent_location_state_code ? (
                    <input type="text" disabled value="" placeholder={t('common.selectStateFirst')} />
                  ) : parentCities.length > 0 ? (
                    <select
                      value={form.parent_city || ''}
                      onChange={(e) => setForm(f => ({ ...f, parent_city: e.target.value }))}
                    >
                      <option value="">{t('common.selectCity')}</option>
                      {parentCities.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={form.parent_city || ''}
                      onChange={(e) => handleChange('parent_city', e.target.value)}
                      placeholder={t('common.enterCity')}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>{t('studentForm.parentZipCode')}</label>
                  <input value={form.parent_zip_code || ''} onChange={(e) => handleChange('parent_zip_code', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.fatherPhone')}</label>
                  <input value={form.father_phone || ''} onChange={(e) => handleChange('father_phone', e.target.value)} placeholder="+33..." />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.motherPhone')}</label>
                  <input value={form.mother_phone || ''} onChange={(e) => handleChange('mother_phone', e.target.value)} placeholder="+33..." />
                </div>
              </div>
            </div>
          )}

          {/* ─── FRANCE-ONLY (by student country): Organization Follow-up (France Travail / Mission Locale) ─── */}
          {isFranceStudent && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>
                {t('studentForm.orgFollowUp.title')}
              </h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.orgFollowUp.subtitle')}
              </p>
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.type')}</label>
                  <select
                    value={form.org_type || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, org_type: v, org_type_other: v === 'Other' ? f.org_type_other : '' }));
                    }}
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="France Travail">{t('studentForm.orgFollowUp.franceTravail', 'France Travail')}</option>
                    <option value="Mission Locale">{t('studentForm.orgFollowUp.missionLocale')}</option>
                    <option value="Other">{t('studentForm.orgFollowUp.other')}</option>
                  </select>
                </div>
                {form.org_type === 'Other' && (
                  <div className="form-group">
                    <label>{t('studentForm.orgFollowUp.otherSpecify')}</label>
                    <input
                      value={form.org_type_other || ''}
                      onChange={(e) => handleChange('org_type_other', e.target.value)}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.town')}</label>
                  <input value={form.org_town || ''} onChange={(e) => handleChange('org_town', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.department')}</label>
                  <input value={form.org_department || ''} onChange={(e) => handleChange('org_department', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.advisorName')}</label>
                  <input value={form.org_advisor_name || ''} onChange={(e) => handleChange('org_advisor_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.advisorEmail')}</label>
                  <input
                    type="email"
                    value={form.org_advisor_email || ''}
                    onChange={(e) => handleChange('org_advisor_email', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.advisorPhone')}</label>
                  <PhoneInput
                    international
                    defaultCountry={config.flagCode}
                    value={
                      form.org_advisor_phone
                        ? `${form.org_advisor_phone_country_code || '+33'}${form.org_advisor_phone}`
                        : undefined
                    }
                    onChange={(value) => {
                      if (!value) {
                        setForm((f) => ({ ...f, org_advisor_phone: '', org_advisor_phone_country_code: '' }));
                        return;
                      }
                      try {
                        const parsed = parsePhoneNumber(value);
                        if (parsed?.countryCallingCode && parsed?.nationalNumber) {
                          setForm((f) => ({
                            ...f,
                            org_advisor_phone: String(parsed.nationalNumber),
                            org_advisor_phone_country_code: `+${parsed.countryCallingCode}`
                          }));
                          return;
                        }
                      } catch {}
                      setForm((f) => ({
                        ...f,
                        org_advisor_phone: value.replace(/^\+/, ''),
                        org_advisor_phone_country_code: ''
                      }));
                    }}
                    placeholder="+33 06 12 34 56 78"
                  />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.registered')}</label>
                  <select
                    value={form.org_registered_france_travail || ''}
                    onChange={(e) => handleChange('org_registered_france_travail', e.target.value)}
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="Yes">{t('common.yes')}</option>
                    <option value="No">{t('common.no')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.registrationDate')}</label>
                  <input
                    type="date"
                    value={form.org_registration_date || ''}
                    onChange={(e) => handleChange('org_registration_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.identificationNumber')}</label>
                  <input
                    value={form.org_identification_number || ''}
                    onChange={(e) => handleChange('org_identification_number', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.compensated')}</label>
                  <select
                    value={form.org_compensated_france_travail || ''}
                    onChange={(e) => handleChange('org_compensated_france_travail', e.target.value)}
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="Yes">{t('common.yes')}</option>
                    <option value="No">{t('common.no')}</option>
                    <option value="Coming soon">{t('studentForm.orgFollowUp.comingSoon')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('studentForm.orgFollowUp.untilDate')}</label>
                  <input
                    type="date"
                    value={form.org_compensated_until_date || ''}
                    onChange={(e) => handleChange('org_compensated_until_date', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ─── FRANCE-ONLY: Studies Followed ─── */}
          {isFranceCRM && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.studiesFollowed')}
              </h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>{t('studentForm.sfLevel')}</th>
                      <th style={{ width: 90 }}>{t('studentForm.sfYear')}</th>
                      <th>{t('studentForm.sfEstablishment')}</th>
                      <th>{t('studentForm.sfDegreeTitle')}</th>
                      <th>{t('studentForm.sfComments')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.studies_followed || []).map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 600 }}>{row.level}</td>
                        <td><input value={row.year} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], year: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.establishment} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], establishment: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.degree_title} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], degree_title: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.comments} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], comments: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── FRANCE-ONLY: Modern Languages ─── */}
          {isFranceCRM && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.modernLanguages')}
              </h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('studentForm.mlLanguage')}</th>
                      <th>{t('studentForm.mlWritten')}</th>
                      <th>{t('studentForm.mlOral')}</th>
                      <th>{t('studentForm.mlDuration')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.modern_languages || []).map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <input value={row.language} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], language: v }; return { ...f, modern_languages: arr }; }); }} placeholder={`${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : 'rd'} language`} style={{ width: '100%' }} />
                        </td>
                        <td>
                          <select value={row.written} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], written: v }; return { ...f, modern_languages: arr }; }); }}>
                            <option value="">{t('common.select')}</option>
                            <option value="Weak">{t('studentForm.mlWeak')}</option>
                            <option value="Medium">{t('studentForm.mlMedium')}</option>
                            <option value="Strong">{t('studentForm.mlStrong')}</option>
                          </select>
                        </td>
                        <td>
                          <select value={row.oral} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], oral: v }; return { ...f, modern_languages: arr }; }); }}>
                            <option value="">{t('common.select')}</option>
                            <option value="Weak">{t('studentForm.mlWeak')}</option>
                            <option value="Medium">{t('studentForm.mlMedium')}</option>
                            <option value="Strong">{t('studentForm.mlStrong')}</option>
                          </select>
                        </td>
                        <td><input value={row.duration} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], duration: v }; return { ...f, modern_languages: arr }; }); }} style={{ width: '100%' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── FRANCE-ONLY: Experience of Stays Abroad ─── */}
          {isFranceCRM && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.staysAbroad')}
              </h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('studentForm.saCountry')}</th>
                      <th>{t('studentForm.saDuration')}</th>
                      <th style={{ width: 90 }}>{t('studentForm.saYear')}</th>
                      <th>{t('studentForm.saActivities')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.stays_abroad || []).map((row, idx) => (
                      <tr key={idx}>
                        <td><input value={row.country} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], country: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.duration} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], duration: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.year} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], year: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.activities} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], activities: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── FRANCE-ONLY: Jobs, Seasonal Work, Internships ─── */}
          {isFranceCRM && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.workInternships')}
              </h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('studentForm.wiCompany')}</th>
                      <th>{t('studentForm.wiDurationYear')}</th>
                      <th>{t('studentForm.wiMissions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.work_internships || []).map((row, idx) => (
                      <tr key={idx}>
                        <td><input value={row.company} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.work_internships || [])]; arr[idx] = { ...arr[idx], company: v }; return { ...f, work_internships: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.duration_year} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.work_internships || [])]; arr[idx] = { ...arr[idx], duration_year: v }; return { ...f, work_internships: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.missions} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.work_internships || [])]; arr[idx] = { ...arr[idx], missions: v }; return { ...f, work_internships: arr }; }); }} style={{ width: '100%' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── FRANCE-ONLY: Cultural, Sports and Associative Activities ─── */}
          {isFranceCRM && !isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.culturalActivities')}
              </h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('studentForm.caActivity')}</th>
                      <th>{t('studentForm.caYears')}</th>
                      <th>{t('studentForm.caLevel')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.cultural_activities || []).map((row, idx) => (
                      <tr key={idx}>
                        <td><input value={row.activity} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.cultural_activities || [])]; arr[idx] = { ...arr[idx], activity: v }; return { ...f, cultural_activities: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.years} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.cultural_activities || [])]; arr[idx] = { ...arr[idx], years: v }; return { ...f, cultural_activities: arr }; }); }} style={{ width: '100%' }} /></td>
                        <td><input value={row.level} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.cultural_activities || [])]; arr[idx] = { ...arr[idx], level: v }; return { ...f, cultural_activities: arr }; }); }} style={{ width: '100%' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── Required Documents (always last) ─── */}
          {!isInterviewerEdit && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--text)', borderBottom: '2px solid var(--border-color)', paddingBottom: 8 }}>
                {t('studentForm.requiredDocs')}
              </h3>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                {t('studentForm.uploadMandatoryDocs')}
              </div>
              {requiredDocuments.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('studentForm.noRequiredDocs')}</div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 12,
                    alignItems: 'stretch'
                  }}
                >
                  {requiredDocuments.map((doc) => {
                    const key = String(doc.document_type_name);
                    const alreadyUploaded = uploadedRequiredDocTypes.includes(key);
                    const selectedFile = requiredDocFiles[key];
                    const inputId = `required-doc-${doc.document_type_id || key.replace(/\s+/g, '-').toLowerCase()}`;
                    return (
                      <div
                        key={key}
                        style={{
                          border: '1px solid var(--border-color)',
                          borderRadius: 8,
                          padding: 12,
                          background: 'var(--card-bg)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                          <div style={{ fontWeight: 600 }}>{doc.document_type_name}</div>
                          <span style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: '#FEE2E2',
                            color: '#991B1B',
                            fontWeight: 600
                          }}>
                            {t('studentForm.requiredBadge')}
                          </span>
                        </div>
                        <input
                          id={inputId}
                          type="file"
                          accept=".pdf,.jpg,.jpeg"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            if (file) {
                              const nextName = String(file.name || '').trim().toLowerCase();
                              const duplicateKey = Object.entries(requiredDocFiles || {}).find(([k, f]) => {
                                if (k === key) return false;
                                const n = String(f?.name || '').trim().toLowerCase();
                                return n && nextName && n === nextName;
                              })?.[0];
                              if (duplicateKey) {
                                toast.error(t('studentForm.duplicateFile', { key: duplicateKey }));
                                e.target.value = '';
                                return;
                              }
                              const sizeErr = getDocumentFileSizeError(file);
                              if (sizeErr) {
                                toast.error(sizeErr);
                                e.target.value = '';
                                return;
                              }
                            }
                            setRequiredDocFiles(prev => ({ ...prev, [key]: file }));
                            setRequiredDocVerification((prev) => ({ ...prev, [key]: '' }));
                            if (file && Number(doc?.is_passport || 0) === 1) {
                              console.log('[Passport Verify][StudentForm] File selected on passport row:', {
                                document_type: key,
                                file_name: file?.name,
                                file_type: file?.type,
                                file_size: file?.size
                              });
                              if (isValidPassportNumber(form.passport_no)) {
                                triggerImmediatePassportVerification(key, file);
                              } else {
                                console.log('[Passport Verify][StudentForm] Skipped: invalid passport number before API call', {
                                  document_type: key,
                                  passport_no: form.passport_no || null
                                });
                                setRequiredDocVerification((prev) => ({ ...prev, [key]: 'not_verified' }));
                              }
                            }
                            if (errors.required_documents) setErrors(er => ({ ...er, required_documents: '' }));
                          }}
                        />
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <label htmlFor={inputId} className="btn btn-outline btn-sm" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {selectedFile ? t('common.replaceFile') : (alreadyUploaded ? t('common.reUploadFile') : t('common.chooseFile'))}
                          </label>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {selectedFile ? selectedFile.name : (alreadyUploaded ? t('common.alreadyUploaded') : t('common.noFileSelected'))}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: alreadyUploaded && !selectedFile ? 'var(--success)' : 'var(--text-muted)' }}>
                          {alreadyUploaded && !selectedFile
                            ? t('studentForm.existingDocAvailable')
                            : t('studentForm.maxFileSizeHint')}
                        </div>
                        {requiredDocVerification[key] === 'verified' ? (
                          <div style={{ fontSize: 12, color: 'var(--success)', fontWeight: 700 }}>
                            {t('studentForm.passportVerified')}
                          </div>
                        ) : null}
                        {requiredDocVerifying[key] ? (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>
                            {t('studentForm.passportVerifying')}
                          </div>
                        ) : null}
                        {requiredDocVerification[key] === 'not_verified' ? (
                          <div style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 700 }}>
                            {t('studentForm.passportNotVerified')}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
              {errors.required_documents && <div className="error-text">{errors.required_documents}</div>}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/students')}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('studentForm.savingBtn') : (isEdit ? t('studentForm.updateBtn') : (studentType === 'b2b' ? t('studentForm.createB2bBtn') : t('studentForm.createBtn')))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentForm;
